import React, { useEffect, useState } from 'react';
import {
  X, CheckCircle2, AlertTriangle, Bot, FileText, Loader2,
  Zap, PauseCircle, XCircle,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import campaignService from '../../services/global/campaignService';

/**
 * Modal didatico de resultado de campanha — abre com transicao suave,
 * mostra 4 cards principais (Enviadas, Falhas, IA pulou, Fallback) e
 * uma lista paginada de recipients quando o usuario clica num card.
 *
 * Tom: enxuto, sem excesso de texto. Reforca confianca mostrando
 * "voce ve tudo o que aconteceu e pode reenviar quando quiser".
 */
const STATUS_LABELS = {
  queued: { label: 'Na fila', color: '#9ca3af', Icon: Loader2 },
  running: { label: 'Em andamento', color: null, Icon: Zap }, // accent
  paused: { label: 'Pausada', color: '#f59e0b', Icon: PauseCircle },
  completed: { label: 'Concluída', color: '#22c55e', Icon: CheckCircle2 },
  failed: { label: 'Com falha', color: '#ea4335', Icon: AlertTriangle },
  cancelled: { label: 'Cancelada', color: '#6b7280', Icon: XCircle },
};

const RECIPIENT_FILTERS = [
  { key: 'sent', label: 'Enviadas', Icon: CheckCircle2, color: '#22c55e' },
  { key: 'failed', label: 'Falhas', Icon: AlertTriangle, color: '#ea4335' },
  { key: 'skipped', label: 'Puladas pela IA', Icon: Bot, color: '#a855f7' },
  // O backend marca como 'sent' quando o fallback HSM foi acionado. Para a
  // V1 deste modal nao temos esse breakdown granular ainda — mostramos o
  // ultimo card com a contagem total de mensagens que cairam em fallback
  // assim que o dispatcher passar a registrar essa metrica separada.
  { key: 'fallback', label: 'Fallback usado', Icon: FileText, color: '#0ea5e9' },
];

const CampaignResultModal = ({ runId, onClose }) => {
  const { colors } = useTheme();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    campaignService.get(runId)
      .then((res) => {
        if (cancelled) return;
        setDoc(res?.data || res);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message || 'Erro ao carregar campanha');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [runId]);

  useEffect(() => {
    const t = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const sent = doc?.sent_count || 0;
  const failed = doc?.failed_count || 0;
  const skipped = doc?.skipped_count || 0;
  const fallback = doc?.fallback_count || 0;
  const status = doc?.status || 'queued';
  const statusMeta = STATUS_LABELS[status] || STATUS_LABELS.queued;
  const StatusIcon = statusMeta.Icon;
  const statusColor = statusMeta.color || colors.accentPrimary;

  const counts = { sent, failed, skipped, fallback };

  // Campanha grande (CAMPAIGN_RECIPIENTS_SCALE_ENABLED): o detalhe traz no
  // maximo 500 destinatarios por status; o total vem em recipients_status_counts.
  const totalNoFiltro = (lista) => {
    const doServidor = doc?.recipients_status_counts?.[activeFilter];
    return typeof doServidor === 'number' ? Math.max(doServidor, lista.length) : lista.length;
  };

  const filteredRecipients = (doc?.recipients_queue || []).filter((r) => {
    if (!activeFilter) return false;
    if (activeFilter === 'fallback') return false; // sem breakdown em V1
    return r.status === activeFilter;
  });

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
        zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        opacity: entered ? 1 : 0,
        transition: 'opacity 250ms ease-out',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: colors.bgPrimary,
          borderRadius: 16,
          width: '100%', maxWidth: 560,
          maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
          transform: entered ? 'translateY(0)' : 'translateY(12px)',
          transition: 'transform 250ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          <div>
            <h3 style={{
              margin: 0, fontSize: 16, fontWeight: 600, color: colors.textPrimary,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <StatusIcon size={18} color={statusColor} />
              {doc?.name || 'Campanha'}
            </h3>
            <p style={{
              margin: '2px 0 0', fontSize: 12, color: colors.textSecondary,
            }}>
              {statusMeta.label}
              {doc?.channel === 'wa_business' ? ' · WhatsApp Business' :
               doc?.channel === 'wa_personal' ? ' · WhatsApp QR' : ''}
              {doc?.total_recipients != null ? ` · ${doc.total_recipients} contatos` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: colors.textSecondary, padding: 8, borderRadius: 8,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 40, color: colors.textSecondary,
            }}>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : error ? (
            <div style={{
              padding: 16, borderRadius: 8,
              backgroundColor: 'rgba(234, 67, 53, 0.1)',
              color: '#ea4335', fontSize: 13,
            }}>
              {error}
            </div>
          ) : (
            <>
              {/* 4 cards principais */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 10,
                marginBottom: 16,
              }}>
                {RECIPIENT_FILTERS.map((f) => {
                  const Icon = f.Icon;
                  const value = counts[f.key];
                  const isActive = activeFilter === f.key;
                  const disabled = f.key === 'fallback';
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => !disabled && setActiveFilter(isActive ? null : f.key)}
                      disabled={disabled}
                      style={{
                        textAlign: 'left',
                        padding: 12,
                        borderRadius: 10,
                        backgroundColor: isActive ? `${f.color}22` : colors.bgSecondary,
                        border: `1px solid ${isActive ? f.color : colors.borderColor}`,
                        cursor: disabled ? 'default' : 'pointer',
                        opacity: disabled ? 0.55 : 1,
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        marginBottom: 4,
                      }}>
                        <Icon size={14} color={f.color} />
                        <span style={{
                          fontSize: 12, color: colors.textSecondary, fontWeight: 500,
                        }}>{f.label}</span>
                      </div>
                      <div style={{
                        fontSize: 22, fontWeight: 700, color: colors.textPrimary,
                      }}>{value}</div>
                    </button>
                  );
                })}
              </div>

              {/* Lista de recipients filtrada */}
              {activeFilter && filteredRecipients.length > 0 && (
                <div style={{
                  border: `1px solid ${colors.borderColor}`,
                  borderRadius: 10,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '8px 12px',
                    backgroundColor: colors.bgSecondary,
                    fontSize: 11, color: colors.textSecondary, fontWeight: 600,
                    borderBottom: `1px solid ${colors.borderColor}`,
                  }}>
                    {totalNoFiltro(filteredRecipients)} contato{totalNoFiltro(filteredRecipients) === 1 ? '' : 's'}
                  </div>
                  <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                    {filteredRecipients.slice(0, 100).map((r, idx) => (
                      <div
                        key={`${r.phone}-${idx}`}
                        style={{
                          padding: '8px 12px',
                          fontSize: 12, color: colors.textPrimary,
                          borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderColor}`,
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}
                      >
                        <span>{r.phone}</span>
                        {r.error && (
                          <span style={{
                            fontSize: 11, color: '#ea4335',
                            maxWidth: 220, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }} title={r.error}>{r.error}</span>
                        )}
                      </div>
                    ))}
                    {totalNoFiltro(filteredRecipients) > 100 && (
                      <div style={{
                        padding: '8px 12px', fontSize: 11,
                        color: colors.textTertiary, fontStyle: 'italic',
                      }}>
                        Mostrando os primeiros 100 de {totalNoFiltro(filteredRecipients)}. A lista completa, com exportação, fica nos destinatários da campanha.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Aviso quando pausada */}
              {status === 'paused' && doc?.paused_until && (
                <div style={{
                  marginTop: 12, padding: 10, borderRadius: 8,
                  backgroundColor: 'rgba(245, 158, 11, 0.12)',
                  border: '1px solid rgba(245, 158, 11, 0.4)',
                  fontSize: 12, color: '#b45309',
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                  <PauseCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    Limite diário do WhatsApp QR atingido. A campanha retoma
                    automaticamente no próximo ciclo.
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignResultModal;
